import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useHistory, useLocation } from 'react-router-dom'

const linkSx = {
  ':hover': {
    color: 'inherit'
  },
  ':focus': {
    outline: 'none',
    textDecoration: 'none',
    color: 'inherit'
  }
}

const isRouteMatch = (pathName, link) => {
  if (!link) return false
  const normalizedPath = String(pathName || '').replace(/\/+$/, '')
  const normalizedLink = String(link || '').replace(/\/+$/, '')
  return normalizedPath === normalizedLink || normalizedPath.startsWith(`${normalizedLink}/`)
}

const checkActiveLink = (config, pathName) => {
  const excludeLinks = config.excludeLinks || []
  const extraLinks = config.extraLinks || []
  const exact = config.exact === true
  const normalizedTo = String(config.to || '').replace(/\/+$/, '')

  if (excludeLinks.some(link => isRouteMatch(pathName, link))) {
    return false
  }

  if (exact) {
    return pathName === normalizedTo || extraLinks.some(link => pathName === String(link || '').replace(/\/+$/, ''))
  }

  return (
    isRouteMatch(pathName, normalizedTo) ||
    extraLinks.some(link => isRouteMatch(pathName, link))
  )
}

const ListItemBody = ({ config, pathName }) => {
  const history = useHistory()
  // programmatically navigate to prevent ripple issues with <a> component
  const navigate = (e) => {
    e.preventDefault()
    if (config.to) {
      history.push(config.to)
    }
  }

  return (
    <ListItemButton component="a" href={config.to} onClick={navigate} selected={checkActiveLink(config, pathName)} sx={linkSx}>
      <ListItemIcon>{config.icon}</ListItemIcon>
      <ListItemText primary={config.title} />
    </ListItemButton>
  )
}

const MenuItem = ({ config, pathName }) => (<ListItem><ListItemBody config={config} pathName={pathName} /></ListItem>)

const ExpandableMenuItem = ({ config, ability, pathName }) => {
  const [open, setOpen] = useState(config.open || false)

  const handleClick = () => {
    setOpen(!open)
  }

  useEffect(() => {
    // Expand the menuitem if the path contains any of its child items.
    const expanded = config.items?.some(configItem => checkActiveLink(configItem, pathName))
    setOpen(expanded)
  }, [config, pathName])

  // Check if any of the items in sub menu are accessible to user then render the parent and those items
  // If there are items without 'ability' defined they should be by default visible
  // Or any of the children items with 'ability' definition are accessible then render the parent
  return (
    (config.items && ([...config.items].filter(i => !Object(i).hasOwnProperty('ability')).length || [...config.items].filter(i => Object(i).hasOwnProperty('ability')).map(item => hasAccess(ability, item)).includes(true))) ? (
      <div component="nav">
        <ListItem onClick={handleClick}>
          <ListItemBody config={config} pathName={pathName} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Menu items={config.items} ability={ability} pathName={pathName} />
        </Collapse>
      </div>
    )
      :
      null
  )
}

const hasAccess = (ability, item) => {
  return ability.can(item.ability?.action, item.ability?.controller)
}

export default function Menu({ items, ability, pathName: incomingPathName }) {
  const location = useLocation()
  const pathName = (incomingPathName || location.pathname || '').replace(/\/+$/, '')

  const createList = (items) => {
    let menu = []
    items.map((menuItem) => {
      if (Array.isArray(menuItem.items) && menuItem.items.length > 0) {
        menu.push(<ExpandableMenuItem
          config={menuItem}
          key={menuItem.title}
          ability={ability}
          pathName={pathName}
        />)
      } else {
        if (!menuItem.ability || hasAccess(ability, menuItem)) {
          menu.push(<MenuItem
            config={menuItem}
            key={menuItem.title}
            pathName={pathName}
          />)
        }
      }
    })
    return menu.concat()
  }

  return <List sx={{ maxHeight: '100vh', overflow: 'auto' }}>{createList(items)}</List>
}

Menu.propTypes = {
  items: PropTypes.array.isRequired,
  ability: PropTypes.object,
  pathName: PropTypes.string
}